// PRD.278 follow-up — automatic Elia context switching (patient ↔ visit scope).
//
// One centralized, pure effective-scope calculation. The React hook
// (useEliaEffectiveScope) supplies route + workspace data; everything decidable
// without hooks lives here so it is unit-testable.
//
//   routeScope       — derived from the active route (patient vs specific visit)
//   manualOverride   — a scope the clinician picked via the Change control
//   effectiveScope   — a valid manualOverride, else the routeScope
//
// The visit id is the ACTUAL clinical visit id (patient_visits.id). When a visit
// is reached through an encounter route, the caller resolves
// patient_encounters.visit_id — never the encounter row id.

import type { EliaContext } from './elia-chat-types';

export type EliaScope =
  | { kind: 'patient'; patientId: string }
  | { kind: 'visit'; patientId: string; visitId: string; visitDate?: string };

/** Stable key that drives conversation isolation/reset. */
export function scopeKey(scope: EliaScope | null): string {
  if (!scope) return 'general';
  return scope.kind === 'visit'
    ? `visit:${scope.patientId}:${scope.visitId}`
    : `patient:${scope.patientId}`;
}

/** Extract patient id and (optional) encounter id from a patient-workspace route. */
export function parsePatientRoute(pathname: string): {
  patientId?: string;
  encounterId?: string;
} {
  const enc = /^\/patient\/([^/]+)\/encounters\/([^/]+)/.exec(pathname);
  if (enc) {
    return { patientId: decodeURIComponent(enc[1]), encounterId: decodeURIComponent(enc[2]) };
  }
  const pat = /^\/patient\/([^/]+)(?:\/|$)/.exec(pathname);
  if (pat && pat[1] !== 'new' && pat[1] !== 'register') {
    return { patientId: decodeURIComponent(pat[1]) };
  }
  return {};
}

/** A manual override is only valid for the SAME patient as the current route. */
export function isOverrideValid(override: EliaScope, routeScope: EliaScope | null): boolean {
  if (!routeScope) return false;
  return override.patientId === routeScope.patientId;
}

/** effectiveScope = valid manualOverride ?? routeScope. */
export function resolveEffectiveScope(
  routeScope: EliaScope | null,
  manualOverride: EliaScope | null,
): EliaScope | null {
  if (manualOverride && isOverrideValid(manualOverride, routeScope)) return manualOverride;
  return routeScope;
}

/** De-identified UI/context shape for a scope. Ids go to the backend; names stay in UI. */
export function scopeToEliaContext(scope: EliaScope | null, patientName?: string): EliaContext {
  if (!scope) return {};
  if (scope.kind === 'visit') {
    return {
      patientId: scope.patientId,
      visitId: scope.visitId,
      patientName,
      contextLabel: scope.visitDate ? `Current visit · ${scope.visitDate}` : 'Current visit',
    };
  }
  return {
    patientId: scope.patientId,
    patientName,
    contextLabel: 'Current patient',
  };
}
